import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import type { EmailConfig } from '@/api/types';
import { REDACTION_SENTINEL } from '@/api/types';
import { Field, FormShell, SecretInput } from './shared';

interface Props {
  initial: EmailConfig | null;
  onSubmit: (config: Partial<EmailConfig>) => void;
  submitting: boolean;
  onCancel: () => void;
  onReset?: () => void;
}

export function EmailForm({ initial, onSubmit, submitting, onCancel, onReset }: Props): JSX.Element {
  const { t } = useTranslation();
  const [host, setHost] = React.useState(initial?.host ?? '');
  const [port, setPort] = React.useState(String(initial?.port ?? 587));
  const [secure, setSecure] = React.useState(initial?.secure ?? false);
  const [user, setUser] = React.useState(initial?.user ?? '');
  const [pass, setPass] = React.useState(initial?.pass ?? '');
  const [fromAddress, setFromAddress] = React.useState(initial?.fromAddress ?? '');
  const [toAddresses, setToAddresses] = React.useState((initial?.toAddresses ?? []).join(', '));

  const preserveOr = (v: string): string =>
    v === REDACTION_SENTINEL || !v ? REDACTION_SENTINEL : v;

  return (
    <form
      id="integration-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          host: host.trim(),
          port: Number(port),
          secure,
          user: user.trim(),
          pass: preserveOr(pass),
          fromAddress: fromAddress.trim(),
          toAddresses: toAddresses
            .split(',')
            .map((a) => a.trim())
            .filter(Boolean),
        });
      }}
    >
      <FormShell submitting={submitting} onCancel={onCancel} onReset={onReset}>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('integrations.form.email.host')} htmlFor="em-host" required>
            <Input id="em-host" value={host} onChange={(e) => setHost(e.target.value)} required />
          </Field>
          <Field label={t('integrations.form.email.port')} htmlFor="em-port" required>
            <Input
              id="em-port"
              type="number"
              min={1}
              max={65535}
              value={port}
              onChange={(e) => setPort(e.target.value)}
              required
            />
          </Field>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox id="em-secure" checked={secure} onCheckedChange={(c) => setSecure(c === true)} />
          <Label htmlFor="em-secure" className="cursor-pointer text-sm font-normal">
            {t('integrations.form.email.secure')}
          </Label>
        </div>
        <Field label={t('integrations.form.email.user')} htmlFor="em-user" required>
          <Input id="em-user" value={user} onChange={(e) => setUser(e.target.value)} required />
        </Field>
        <Field label={t('integrations.form.email.pass')} htmlFor="em-pass" required hint={t('integrations.form.preserveHint')}>
          <SecretInput id="em-pass" value={pass} onChange={setPass} />
        </Field>
        <Field label={t('integrations.form.email.fromAddress')} htmlFor="em-from" required>
          <Input
            id="em-from"
            type="email"
            value={fromAddress}
            onChange={(e) => setFromAddress(e.target.value)}
            required
          />
        </Field>
        <Field
          label={t('integrations.form.email.toAddresses')}
          htmlFor="em-to"
          required
          hint={t('integrations.form.email.toAddressesHint')}
        >
          <Input
            id="em-to"
            value={toAddresses}
            onChange={(e) => setToAddresses(e.target.value)}
            placeholder="sav@acme.com, support@acme.com"
            required
          />
        </Field>
      </FormShell>
    </form>
  );
}
